import { motion } from 'framer-motion';
import { Button } from '@/components/Button';
import { useRouter } from 'next/router';
import Link from 'next/link';
import {
    AdjustmentsHorizontalIcon,
    ChartBarIcon,
    MegaphoneIcon,
    PuzzlePieceIcon,
    RocketLaunchIcon,
    UsersIcon,
    CurrencyDollarIcon,
    LockClosedIcon,
    ClipboardDocumentCheckIcon,
    GiftIcon,
    Cog6ToothIcon,
    BuildingOfficeIcon,
    CodeBracketIcon,
    WalletIcon,
    BanknotesIcon,
    CheckCircleIcon,
    ShoppingCartIcon,
    GlobeAltIcon,
    CreditCardIcon,
    ClipboardDocumentListIcon,
    BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';
import SEOMeta from '@/templates/SEOMeta';
import { SeoInternalLinks } from '@/components/SeoInternalLinks';
import { useTranslations } from 'next-intl';
import { getI18nProps } from '@/utils/i18n';
export async function getStaticProps(ctx) {
  const { getI18nPropsWithSeo } = await import('@/utils/i18n');
  return getI18nPropsWithSeo(ctx, 'detailedOverview', '/detailed-overview');
}

export default function DetailedOverview({ seoMeta }) {
    const router = useRouter();
    const t = useTranslations('detailedOverview');

    // Arrays moved inside component to use translations (no UI changes)
    const whoItsFor = [
        { key: 'retail', title: t('whoItsFor.retail.title'), description: t('whoItsFor.retail.description') },
        { key: 'fashion', title: t('whoItsFor.fashion.title'), description: t('whoItsFor.fashion.description') },
        { key: 'travel', title: t('whoItsFor.travel.title'), description: t('whoItsFor.travel.description') },
        { key: 'finance', title: t('whoItsFor.finance.title'), description: t('whoItsFor.finance.description') },
        { key: 'telco', title: t('whoItsFor.telco.title'), description: t('whoItsFor.telco.description') },
        { key: 'b2b', title: t('whoItsFor.b2b.title'), description: t('whoItsFor.b2b.description') },
    ];

    const completeWorkflow = [
        {
            phaseKey: 'phase1',
            phase: t('workflow.phases.phase1.title'),
            steps: [
                {
                    number: 1,
                    stepKey: 'companyProfileSetup',
                    title: t('workflow.phases.phase1.steps.companyProfileSetup.title'),
                    icon: BuildingOfficeIcon,
                    description: t('workflow.phases.phase1.steps.companyProfileSetup.description'),
                    details: t('workflow.phases.phase1.steps.companyProfileSetup.details'),
                    color: 'from-blue-500 to-blue-600',
                },
                {
                    number: 2,
                    stepKey: 'currencyPaymentConfiguration',
                    title: t('workflow.phases.phase1.steps.currencyPaymentConfiguration.title'),
                    icon: CurrencyDollarIcon,
                    description: t('workflow.phases.phase1.steps.currencyPaymentConfiguration.description'),
                    details: t('workflow.phases.phase1.steps.currencyPaymentConfiguration.details'),
                    color: 'from-green-500 to-green-600',
                },
                {
                    number: 3,
                    stepKey: 'platformIntegration',
                    title: t('workflow.phases.phase1.steps.platformIntegration.title'),
                    icon: PuzzlePieceIcon,
                    description: t('workflow.phases.phase1.steps.platformIntegration.description'),
                    details: t('workflow.phases.phase1.steps.platformIntegration.details'),
                    color: 'from-purple-500 to-purple-600',
                },
                {
                    number: 4,
                    stepKey: 'domainVerification',
                    title: t('workflow.phases.phase1.steps.domainVerification.title'),
                    icon: LockClosedIcon,
                    description: t('workflow.phases.phase1.steps.domainVerification.description'),
                    details: t('workflow.phases.phase1.steps.domainVerification.details'),
                    color: 'from-red-500 to-red-600',
                },
            ],
        },
        {
            phaseKey: 'phase2',
            phase: t('workflow.phases.phase2.title'),
            steps: [
                {
                    number: 5,
                    stepKey: 'campaignStrategy',
                    title: t('workflow.phases.phase2.steps.campaignStrategy.title'),
                    icon: RocketLaunchIcon,
                    description: t('workflow.phases.phase2.steps.campaignStrategy.description'),
                    details: t('workflow.phases.phase2.steps.campaignStrategy.details'),
                    color: 'from-orange-500 to-orange-600',
                },
                {
                    number: 6,
                    stepKey: 'rewardStructureDesign',
                    title: t('workflow.phases.phase2.steps.rewardStructureDesign.title'),
                    icon: GiftIcon,
                    description: t('workflow.phases.phase2.steps.rewardStructureDesign.description'),
                    details: t('workflow.phases.phase2.steps.rewardStructureDesign.details'),
                    color: 'from-pink-500 to-pink-600',
                },
                {
                    number: 7,
                    stepKey: 'advancedCampaignSettings',
                    title: t('workflow.phases.phase2.steps.advancedCampaignSettings.title'),
                    icon: Cog6ToothIcon,
                    description: t('workflow.phases.phase2.steps.advancedCampaignSettings.description'),
                    details: t('workflow.phases.phase2.steps.advancedCampaignSettings.details'),
                    color: 'from-indigo-500 to-indigo-600',
                },
                {
                    number: 8,
                    stepKey: 'campaignLaunch',
                    title: t('workflow.phases.phase2.steps.campaignLaunch.title'),
                    icon: MegaphoneIcon,
                    description: t('workflow.phases.phase2.steps.campaignLaunch.description'),
                    details: t('workflow.phases.phase2.steps.campaignLaunch.details'),
                    color: 'from-teal-500 to-teal-600',
                },
            ],
        },
        {
            phaseKey: 'phase3',
            phase: t('workflow.phases.phase3.title'),
            steps: [
                {
                    number: 9,
                    stepKey: 'affiliateOnboarding',
                    title: t('workflow.phases.phase3.steps.affiliateOnboarding.title'),
                    icon: UsersIcon,
                    description: t('workflow.phases.phase3.steps.affiliateOnboarding.description'),
                    details: t('workflow.phases.phase3.steps.affiliateOnboarding.details'),
                    color: 'from-cyan-500 to-cyan-600',
                },
                {
                    number: 10,
                    stepKey: 'affiliateManagement',
                    title: t('workflow.phases.phase3.steps.affiliateManagement.title'),
                    icon: ClipboardDocumentCheckIcon,
                    description: t('workflow.phases.phase3.steps.affiliateManagement.description'),
                    details: t('workflow.phases.phase3.steps.affiliateManagement.details'),
                    color: 'from-lime-500 to-lime-600',
                },
                {
                    number: 11,
                    stepKey: 'affiliateSupportCommunication',
                    title: t('workflow.phases.phase3.steps.affiliateSupportCommunication.title'),
                    icon: UsersIcon,
                    description: t('workflow.phases.phase3.steps.affiliateSupportCommunication.description'),
                    details: t('workflow.phases.phase3.steps.affiliateSupportCommunication.details'),
                    color: 'from-emerald-500 to-emerald-600',
                },
            ],
        },
        {
            phaseKey: 'phase4',
            phase: t('workflow.phases.phase4.title'),
            steps: [
                {
                    number: 12,
                    stepKey: 'realTimeTracking',
                    title: t('workflow.phases.phase4.steps.realTimeTracking.title'),
                    icon: ChartBarIcon,
                    description: t('workflow.phases.phase4.steps.realTimeTracking.description'),
                    details: t('workflow.phases.phase4.steps.realTimeTracking.details'),
                    color: 'from-violet-500 to-violet-600',
                },
                {
                    number: 13,
                    stepKey: 'performanceAnalytics',
                    title: t('workflow.phases.phase4.steps.performanceAnalytics.title'),
                    icon: AdjustmentsHorizontalIcon,
                    description: t('workflow.phases.phase4.steps.performanceAnalytics.description'),
                    details: t('workflow.phases.phase4.steps.performanceAnalytics.details'),
                    color: 'from-rose-500 to-rose-600',
                },
                {
                    number: 14,
                    stepKey: 'revenueAttribution',
                    title: t('workflow.phases.phase4.steps.revenueAttribution.title'),
                    icon: BanknotesIcon,
                    description: t('workflow.phases.phase4.steps.revenueAttribution.description'),
                    details: t('workflow.phases.phase4.steps.revenueAttribution.details'),
                    color: 'from-amber-500 to-amber-600',
                },
            ],
        },
        {
            phaseKey: 'phase5',
            phase: t('workflow.phases.phase5.title'),
            steps: [
                {
                    number: 15,
                    stepKey: 'commissionProcessing',
                    title: t('workflow.phases.phase5.steps.commissionProcessing.title'),
                    icon: CurrencyDollarIcon,
                    description: t('workflow.phases.phase5.steps.commissionProcessing.description'),
                    details: t('workflow.phases.phase5.steps.commissionProcessing.details'),
                    color: 'from-blue-500 to-blue-600',
                },
                {
                    number: 16,
                    stepKey: 'multiChannelPayouts',
                    title: t('workflow.phases.phase5.steps.multiChannelPayouts.title'),
                    icon: WalletIcon,
                    description: t('workflow.phases.phase5.steps.multiChannelPayouts.description'),
                    details: t('workflow.phases.phase5.steps.multiChannelPayouts.details'),
                    color: 'from-green-500 to-green-600',
                },
                {
                    number: 17,
                    stepKey: 'programOptimization',
                    title: t('workflow.phases.phase5.steps.programOptimization.title'),
                    icon: ChartBarIcon,
                    description: t('workflow.phases.phase5.steps.programOptimization.description'),
                    details: t('workflow.phases.phase5.steps.programOptimization.details'),
                    color: 'from-purple-500 to-purple-600',
                },
                {
                    number: 18,
                    stepKey: 'scaleGrowth',
                    title: t('workflow.phases.phase5.steps.scaleGrowth.title'),
                    icon: RocketLaunchIcon,
                    description: t('workflow.phases.phase5.steps.scaleGrowth.description'),
                    details: t('workflow.phases.phase5.steps.scaleGrowth.details'),
                    color: 'from-orange-500 to-orange-600',
                },
            ],
        },
    ];

    const quickResources = [
        { key: 'campaignCreationGuide', title: t('quickResources.campaignCreationGuide.title'), tag: t('quickResources.campaignCreationGuide.tag'), description: t('quickResources.campaignCreationGuide.description') },
        { key: 'affiliateManagementTutorial', title: t('quickResources.affiliateManagementTutorial.title'), tag: t('quickResources.affiliateManagementTutorial.tag'), description: t('quickResources.affiliateManagementTutorial.description') },
        { key: 'apiIntegrationDocumentation', title: t('quickResources.apiIntegrationDocumentation.title'), tag: t('quickResources.apiIntegrationDocumentation.tag'), description: t('quickResources.apiIntegrationDocumentation.description') },
        { key: 'trackingSetupInstructions', title: t('quickResources.trackingSetupInstructions.title'), tag: t('quickResources.trackingSetupInstructions.tag'), description: t('quickResources.trackingSetupInstructions.description') },
        { key: 'payoutProcessingGuide', title: t('quickResources.payoutProcessingGuide.title'), tag: t('quickResources.payoutProcessingGuide.tag'), description: t('quickResources.payoutProcessingGuide.description') },
        { key: 'bestPracticesPlaybook', title: t('quickResources.bestPracticesPlaybook.title'), tag: t('quickResources.bestPracticesPlaybook.tag'), description: t('quickResources.bestPracticesPlaybook.description') },
    ];

    const afivioRoleChips = [
        'clicks',
        'registrations',
        'purchases',
        'revenue',
        'attribution',
        'commissions',
        'payoutReadyReporting',
        'referralCodes',
    ];

    const integrationMethods = [
        {
            key: 'shopify',
            title: 'Shopify Integration',
            subtitle: 'Official Shopify app',
            description: 'Native Shopify onboarding with OAuth, order sync and campaign tracking inside one workflow.',
            href: '/shopify-integration',
            icon: ShoppingCartIcon,
            tone: 'blue'
        },
        {
            key: 'wordpress',
            title: 'WordPress / WooCommerce',
            subtitle: 'Plugin-based setup',
            description: 'Install the Afivio plugin to track clicks, signups, coupons and paid-order commissions.',
            href: '/wordpress-woocommerce-integration',
            icon: GlobeAltIcon,
            tone: 'indigo'
        },
        {
            key: 'stripe',
            title: 'Stripe Billing',
            subtitle: 'Payment-level attribution',
            description: 'Connect Stripe and automatically map qualified payments to affiliates and campaigns.',
            href: '/stripe-integration',
            icon: CreditCardIcon,
            tone: 'violet'
        },
        {
            key: 'custom',
            title: 'Custom Script / API',
            subtitle: 'Any tech stack',
            description: 'Use JavaScript tracking and REST API endpoints for flexible custom integration.',
            href: '/custom-api-integration',
            icon: CodeBracketIcon,
            tone: 'sky'
        },
        {
            key: 'manual',
            title: 'Manual Dashboard Mode',
            subtitle: 'No-code launch',
            description: 'Run affiliate operations manually in dashboard and switch to automation later.',
            href: '/manual-integration',
            icon: ClipboardDocumentListIcon,
            tone: 'emerald'
        },
        {
            key: 'horoshop',
            title: 'Horoshop Integration',
            subtitle: 'Webhook + script flow',
            description: 'Dedicated setup for Horoshop with host, token and automated webhook subscriptions.',
            href: '/horoshop-integration',
            icon: BuildingStorefrontIcon,
            tone: 'cyan'
        }
    ];

    return (
        <>
            <SEOMeta
                title={seoMeta?.title || t('seo.title')}
                description={seoMeta?.description || t('seo.description')}
                keywords={seoMeta?.keywords || t('seo.keywords')}
                img="/og.png"
                url={seoMeta?.url || "/detailed-overview"}
            />

            <div className="bg-gradient-to-b from-slate-50 via-white to-white overflow-x-hidden">
                {/* Hero Section */}
                <section className="pt-24 pb-16 px-6 bg-gradient-to-r from-blue-50 to-purple-50">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex flex-col lg:flex-row lg:items-stretch gap-8 lg:gap-12">
                            {/* Left */}
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="lg:w-1/2 flex flex-col"
                            >
                                <div>
                  <span className="inline-block bg-white text-blue-600 text-sm font-semibold px-4 py-2 rounded-full mb-4 border border-blue-200">
                    {t('hero.badge')}
                  </span>
                                    <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
                                        {t('hero.title')}
                                    </h1>
                                </div>

                                <p className="text-lg text-gray-600 leading-relaxed mt-4">
                                    {t('hero.paragraph1')}
                                </p>

                                <p className="text-lg text-gray-600 leading-relaxed mt-4">
                                    {t('hero.paragraph2')}
                                </p>

                                <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-200 mt-6 flex-grow">
                                    <h3 className="text-lg font-bold mb-3 text-gray-900">
                                        {t('hero.card.title')}
                                    </h3>
                                    <div className="space-y-3">
                                        <div className="flex items-center">
                                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                                            <span className="text-gray-700">{t('hero.card.bullets.trial')}</span>
                                        </div>
                                        <div className="flex items-center">
                                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                                            <span className="text-gray-700">{t('hero.card.bullets.help')}</span>
                                        </div>
                                        <div className="flex items-center">
                                            <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                                            <span className="text-gray-700">{t('hero.card.bullets.payResults')}</span>
                                        </div>
                                    </div>
                                </div>

                                <Button blue onClick={() => router.push('/signup')} className="w-full sm:w-auto mt-6">
                                    {t('hero.cta')}
                                </Button>
                            </motion.div>

                            {/* Right */}
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="lg:w-1/2 flex"
                            >
                                <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-8 text-white shadow-2xl w-full flex flex-col justify-center">
                                    <div className="text-center mb-8">
                                        <h3 className="text-2xl font-bold mb-4">{t('truth.title')}</h3>
                                        <p className="text-blue-100">{t('truth.subtitle')}</p>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="bg-white/10 rounded-xl p-4">
                                            <div className="text-3xl font-bold mb-2">95%</div>
                                            <div className="text-sm">{t('truth.metrics.launch')}</div>
                                        </div>
                                        <div className="bg-white/10 rounded-xl p-4">
                                            <div className="text-3xl font-bold mb-2">24/7</div>
                                            <div className="text-sm">{t('truth.metrics.support')}</div>
                                        </div>
                                        <div className="bg-white/10 rounded-xl p-4">
                                            <div className="text-3xl font-bold mb-2">$0</div>
                                            <div className="text-sm">{t('truth.metrics.risk')}</div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Who is our platform for? */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="text-center mb-12">
                            <h2 className="text-4xl font-bold mb-4 text-gray-900">{t('whoSection.title')}</h2>
                            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                                {t('whoSection.subtitle')}
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {whoItsFor.map((item, idx) => (
                                <motion.div
                                    key={item.key}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.05 }}
                                    className="bg-gray-50 rounded-2xl p-6 border border-gray-200 hover:shadow-md transition-all"
                                >
                                    <h3 className="text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.description}</p>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mt-10 max-w-3xl mx-auto text-center text-gray-600">
                            <p>
                                {t('whoSection.note.beforeEmail')}{' '}
                                <a
                                    href="mailto:contact@afivio.com"
                                    className="font-medium text-blue-600 underline underline-offset-4"
                                >
                                    contact@afivio.com
                                </a>
                                {t('whoSection.note.afterEmail')}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Complete Workflow */}
                <section className="py-20 bg-slate-50">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold mb-4">{t('workflowSection.title')}</h2>
                            <p className="text-gray-600 max-w-3xl mx-auto">{t('workflowSection.subtitle')}</p>
                        </div>

                        <div className="space-y-12">
                            {completeWorkflow.map((phase, phaseIdx) => (
                                <motion.div
                                    key={phase.phaseKey}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    className="bg-white rounded-3xl shadow-lg overflow-hidden border border-gray-100"
                                >
                                    <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-8">
                                        <div className="flex items-center">
                                            <div className="bg-white text-gray-900 font-bold rounded-full w-10 h-10 flex items-center justify-center mr-4">
                                                {phaseIdx + 1}
                                            </div>
                                            <h3 className="text-2xl font-bold text-white">{phase.phase}</h3>
                                        </div>
                                    </div>

                                    <div className="p-8">
                                        <div className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6">
                                            {phase.steps.map((step) => {
                                                const Icon = step.icon;
                                                return (
                                                    <motion.div
                                                        key={step.stepKey}
                                                        whileHover={{ y: -4 }}
                                                        className="border border-gray-200 rounded-2xl p-6 hover:shadow-md transition-all bg-white"
                                                    >
                                                        <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${step.color} mb-4`}>
                                                            <Icon className="h-6 w-6 text-white" />
                                                        </div>
                                                        <div className="text-sm font-semibold text-gray-500 mb-2">
                                                            {t('workflowSection.stepLabel', { number: step.number })}
                                                        </div>
                                                        <h4 className="text-lg font-bold mb-3 text-gray-900">{step.title}</h4>
                                                        <p className="text-gray-700 mb-4 text-sm">{step.description}</p>
                                                        <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg border border-gray-100">
                                                            {step.details}
                                                        </div>
                                                    </motion.div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Affiliate & Referral Marketing Basics */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="text-center mb-14">
              <span className="inline-block bg-white text-blue-600 text-sm font-semibold px-4 py-2 rounded-full mb-4 border border-blue-200">
                {t('basics.badge')}
              </span>
                            <h2 className="text-4xl font-bold mb-4 text-gray-900">{t('basics.title')}</h2>
                            <p className="text-gray-600 max-w-3xl mx-auto text-lg">{t('basics.subtitle')}</p>
                        </div>

                        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
                            {/* Affiliate */}
                            <motion.div
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                                className="bg-gray-50 rounded-3xl p-8 border border-gray-200 shadow-sm hover:shadow-md transition-all"
                            >
                                <div className="flex items-start gap-4 mb-6">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-blue-50">
                                        <MegaphoneIcon className="h-7 w-7 text-blue-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900">{t('basics.affiliate.title')}</h3>
                                        <p className="text-gray-600 mt-1">{t('basics.affiliate.subtitle')}</p>
                                    </div>
                                </div>

                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                                        <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                                            {t('basics.affiliate.cards.typicalReward.label')}
                                        </p>
                                        <p className="mt-2 text-sm text-gray-700">{t('basics.affiliate.cards.typicalReward.text')}</p>
                                    </div>
                                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                                        <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                                            {t('basics.affiliate.cards.howShared.label')}
                                        </p>
                                        <p className="mt-2 text-sm text-gray-700">{t('basics.affiliate.cards.howShared.text')}</p>
                                    </div>
                                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                                        <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                                            {t('basics.affiliate.cards.bestFor.label')}
                                        </p>
                                        <p className="mt-2 text-sm text-gray-700">{t('basics.affiliate.cards.bestFor.text')}</p>
                                    </div>
                                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                                        <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                                            {t('basics.affiliate.cards.keyFocus.label')}
                                        </p>
                                        <p className="mt-2 text-sm text-gray-700">{t('basics.affiliate.cards.keyFocus.text')}</p>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Referral */}
                            <motion.div
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.05 }}
                                className="bg-gray-50 rounded-3xl p-8 border border-gray-200 shadow-sm hover:shadow-md transition-all"
                            >
                                <div className="flex items-start gap-4 mb-6">
                                    <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-purple-50">
                                        <GiftIcon className="h-7 w-7 text-purple-600" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900">{t('basics.referral.title')}</h3>
                                        <p className="text-gray-600 mt-1">{t('basics.referral.subtitle')}</p>
                                    </div>
                                </div>

                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                                        <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                                            {t('basics.referral.cards.typicalReward.label')}
                                        </p>
                                        <p className="mt-2 text-sm text-gray-700">{t('basics.referral.cards.typicalReward.text')}</p>
                                    </div>
                                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                                        <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                                            {t('basics.referral.cards.howShared.label')}
                                        </p>
                                        <p className="mt-2 text-sm text-gray-700">{t('basics.referral.cards.howShared.text')}</p>
                                    </div>
                                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                                        <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                                            {t('basics.referral.cards.bestFor.label')}
                                        </p>
                                        <p className="mt-2 text-sm text-gray-700">{t('basics.referral.cards.bestFor.text')}</p>
                                    </div>
                                    <div className="bg-white rounded-2xl border border-gray-200 p-5">
                                        <p className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                                            {t('basics.referral.cards.keyFocus.label')}
                                        </p>
                                        <p className="mt-2 text-sm text-gray-700">{t('basics.referral.cards.keyFocus.text')}</p>
                                    </div>
                                </div>
                            </motion.div>
                        </div>

                        {/* Afivio role */}
                        <motion.div
                            initial={{ opacity: 0, y: 22 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45 }}
                            className="mt-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 md:p-10 text-white shadow-xl"
                        >
                            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
                                <div className="max-w-2xl">
                                    <h3 className="text-2xl md:text-3xl font-bold">{t('basics.afivioRole.title')}</h3>
                                    <p className="mt-2 text-white/80 text-base">{t('basics.afivioRole.subtitle')}</p>

                                    <div className="mt-6 flex flex-wrap gap-2">
                                        {afivioRoleChips.map((chipKey) => (
                                            <span
                                                key={chipKey}
                                                className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white border border-white/15"
                                            >
                        {t(`basics.afivioRole.chips.${chipKey}`)}
                      </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="grid sm:grid-cols-2 gap-4 w-full lg:w-[520px]">
                                    <div className="rounded-2xl bg-white/10 border border-white/15 p-5">
                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/15">
                                                <ClipboardDocumentCheckIcon className="h-6 w-6 text-white" />
                                            </div>
                                            <p className="text-sm font-bold">{t('basics.afivioRole.helps.title')}</p>
                                        </div>
                                        <ul className="mt-3 space-y-2 text-sm text-white/85">
                                            <li>{t('basics.afivioRole.helps.items.track')}</li>
                                            <li>{t('basics.afivioRole.helps.items.rules')}</li>
                                            <li>{t('basics.afivioRole.helps.items.commissions')}</li>
                                            <li>{t('basics.afivioRole.helps.items.reporting')}</li>
                                        </ul>
                                    </div>

                                    <div className="rounded-2xl bg-white/10 border border-white/15 p-5">
                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-white/15">
                                                <LockClosedIcon className="h-6 w-6 text-white" />
                                            </div>
                                            <p className="text-sm font-bold">{t('basics.afivioRole.doesNot.title')}</p>
                                        </div>
                                        <ul className="mt-3 space-y-2 text-sm text-white/85">
                                            <li>{t('basics.afivioRole.doesNot.items.buyTraffic')}</li>
                                            <li>{t('basics.afivioRole.doesNot.items.runAds')}</li>
                                            <li>{t('basics.afivioRole.doesNot.items.replace')}</li>
                                            <li>{t('basics.afivioRole.doesNot.items.changeCheckout')}</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                {['trackableActions', 'worksWith', 'attribution', 'outcome'].map((k) => (
                                    <div key={k} className="rounded-2xl bg-white/10 border border-white/15 p-4">
                                        <p className="text-[11px] uppercase tracking-wide text-white/70 font-semibold">
                                            {t(`basics.afivioRole.miniMetrics.${k}.label`)}
                                        </p>
                                        <p className="mt-2 text-sm font-semibold text-white">
                                            {t(`basics.afivioRole.miniMetrics.${k}.value`)}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Integration Methods */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="text-center mb-12">
                            <h2 className="text-4xl font-bold mb-4">{t('integration.title')}</h2>
                            <p className="text-gray-600 max-w-3xl mx-auto">
                                Available methods you can use today, depending on your platform and setup depth.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch mb-12">
                            {integrationMethods.map((method, index) => {
                                const Icon = method.icon;
                                const toneClasses = {
                                    blue: 'border-blue-100 bg-blue-50 text-blue-700',
                                    indigo: 'border-indigo-100 bg-indigo-50 text-indigo-700',
                                    violet: 'border-violet-100 bg-violet-50 text-violet-700',
                                    sky: 'border-sky-100 bg-sky-50 text-sky-700',
                                    emerald: 'border-emerald-100 bg-emerald-50 text-emerald-700',
                                    cyan: 'border-cyan-100 bg-cyan-50 text-cyan-700'
                                };
                                const tone = toneClasses[method.tone] || toneClasses.blue;

                                return (
                                    <motion.div
                                        key={method.key}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="h-full"
                                    >
                                        <Link
                                            href={method.href}
                                            className="group h-full block rounded-2xl border border-gray-200 bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md"
                                        >
                                            <div className="mb-5 flex items-center justify-between">
                                                <div className="flex items-center">
                                                    <div className={`mr-3 flex h-10 w-10 items-center justify-center rounded-xl border ${tone}`}>
                                                        <Icon className="h-6 w-6" />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-lg font-bold text-gray-900">{method.title}</h3>
                                                        <p className="text-xs text-gray-500">{method.subtitle}</p>
                                                    </div>
                                                </div>
                                                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                          Available
                        </span>
                                            </div>

                                            <p className="mb-4 text-sm text-gray-700">{method.description}</p>

                                            <div className="mt-auto text-sm font-semibold text-blue-600 group-hover:text-blue-700">
                                                Open integration page →
                                            </div>
                                        </Link>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Quick Links */}
                <section className="py-16 bg-slate-50">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="text-center mb-10">
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('quickLinks.title')}</h3>
                            <p className="text-gray-600 text-sm max-w-xl mx-auto">{t('quickLinks.subtitle')}</p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                            {quickResources.map((item, idx) => (
                                <motion.div
                                    key={item.key}
                                    initial={{ opacity: 0, y: 15 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all flex flex-col"
                                >
                                    <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center justify-center text-xs font-semibold text-gray-700 bg-gray-100 rounded-full px-3 py-1">
                      {idx + 1 < 10 ? `0${idx + 1}` : idx + 1}
                    </span>
                                        <span className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
                      {item.tag}
                    </span>
                                    </div>
                                    <div className="flex items-start mb-3">
                                        <LockClosedIcon className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                                        <h4 className="text-sm font-semibold text-gray-900">{item.title}</h4>
                                    </div>
                                    <p className="text-xs text-gray-600 mt-auto">{item.description}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Final CTA */}
                <section className="py-24">
                    <div className="max-w-7xl mx-auto px-6 text-center">
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            whileInView={{ scale: 1, opacity: 1 }}
                            className="bg-gradient-to-r from-blue-600 to-purple-600 p-12 rounded-3xl text-white shadow-xl"
                        >
                            <h2 className="text-4xl font-bold mb-6">{t('finalCta.title')}</h2>
                            <p className="text-xl mb-8 max-w-2xl mx-auto">{t('finalCta.subtitle')}</p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <Button
                                    white
                                    onClick={() => router.push('/signup')}
                                    className="bg-white text-blue-600 px-8 py-4 rounded-xl hover:bg-gray-100 font-semibold"
                                >
                                    {t('finalCta.button')}
                                </Button>
                            </div>
                            <p className="mt-6 text-blue-100 text-sm">{t('finalCta.note1')}</p>
                            <p className="mt-3 text-blue-100 text-sm">
                                {t('finalCta.note2.beforeEmail')}{' '}
                                <a
                                    href="mailto:contact@afivio.com"
                                    className="font-medium text-white underline underline-offset-4"
                                >
                                    contact@afivio.com
                                </a>
                                {t('finalCta.note2.afterEmail')}
                            </p>
                        </motion.div>
                    </div>
                </section>

                <SeoInternalLinks />
            </div>
        </>
    );
}
